import React from "react";
import { useNavigate } from "react-router-dom";
import "./aboutus.css";
import { CDN_BASE_URL, cdnUrl } from "../../config/cdn";

// Import JSON
import aboutData from "./aboutus.json";

// ---------------- Generic Reusable Side-by-side Layout ----------------
const SideBySide = ({ leftContent, rightContent, bgColor, textColor, leftFlex = 1, rightFlex = 1, gap = "40px" }) => (
  <div
    className="side-by-side"
    style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: bgColor,
      color: textColor,
      padding: "60px 20px",
      gap,
    }}
  >
    <div className="left-col" style={{ flex: leftFlex }}>{leftContent}</div>
    <div className="right-col" style={{ flex: rightFlex, textAlign: "left" }}>{rightContent}</div>
  </div>
);

// ---------------- HERO IMAGE ----------------
const HeroImage = ({ title, background }) => {
  // Convert: "/assets/images/xyz.jpg" → "/images/xyz.jpg"
  const finalBg = background.replace("/assets", "");

  return (
    <div
      style={{
        backgroundImage: `url("${CDN_BASE_URL}${finalBg}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "600px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        marginBottom: "50px",
      }}
    >
      <h1
        style={{
          color: "#fff",
          fontSize: "4rem",
          fontWeight: "bold",
          textShadow: "2px 2px 8px rgba(0,0,0,0.7)",
          textAlign: "center",
        }}
        dangerouslySetInnerHTML={{ __html: title }}
      />
    </div>
  );
};


// ---------------- WHO WE ARE ----------------
const WhoWeAre = ({ data }) => (
  <SideBySide
    bgColor={data.bgColor}
    textColor={data.textColor}
    leftFlex={0.5}
    rightFlex={1.5}
    gap="80px"
    leftContent={
      <h2
        className="about-heading"
        style={{ fontSize: "3rem", fontWeight: "bold", lineHeight: "1.2", color: "#006666" }}
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
    }
    rightContent={<p style={{ fontSize: "1.1rem", lineHeight: "1.8rem" }}>{data.text}</p>}
  />
);

// ---------------- MISSION & VISION ----------------
const MissionVision = ({ data }) => (
  <SideBySide
    bgColor={data.bgColor}
    textColor={data.textColor}
    leftContent={
      <img
         src={`${CDN_BASE_URL}${data.image.replace("/assets", "")}`} 
        alt="Mission Vision"
        style={{
          width: "95%",
          height: "450px",
          borderRadius: "8px",
          objectFit: "cover",
        }}
      />
    }
    rightContent={
      <div style={{ lineHeight: "1.8" }}>
        <h2 style={{ fontSize: "4rem", marginBottom: "1rem", color: "#006666" }}>{data.title}</h2>
        <p style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>{data.paragraph1}</p>
        <p style={{ fontSize: "1.1rem" }}>{data.paragraph2}</p>
      </div>
    }
  />
);

// ---------------- WHY MEDRCM ----------------
const WhyMedRCM = ({ data }) => {
  // Convert: "/assets/images/ambulatory.png" → "/images/ambulatory.png"
  const finalImage = data.image.replace("/assets", "");

  return (
    <SideBySide
      bgColor={data.bgColor}
      textColor={data.textColor}
      leftContent={
        <img
          src={`${CDN_BASE_URL}${finalImage}`}
          alt="Why MedRCM"
          style={{
            width: "98%",
            height: "500px",
            borderRadius: "8px",
            objectFit: "cover",
          }}
        />
      }
      rightContent={
        <div style={{ lineHeight: "1.8" }}>
          <h2
            style={{
              fontSize: "4rem",
              marginBottom: "1rem",
              color: "#006666",
            }}
          >
            {data.title}
          </h2>
          <p style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>
            {data.paragraph1}
          </p>
          <p style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>
            {data.paragraph2}
          </p>
          <p style={{ fontSize: "1.1rem" }}>{data.paragraph3}</p>
        </div>
      }
    />
  );
};


// ---------------- FEATURES ----------------
const Features = ({ data }) => (
  <section style={{ backgroundColor: data.bgColor, color: data.textColor, padding: "60px 20px" }}>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "40px", justifyContent: "space-between" }}>
      {data.items.map((f, i) => (
        <div key={i} style={{ flex: "1 1 200px", minWidth: "200px" }}>
          <img src={cdnUrl(f.icon)} alt={f.title} style={{ width: "50px", marginBottom: "10px" }} />
          <h3>{f.title}</h3>
          <p>{f.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

// ---------------- EXCELLENCE ----------------
const ExcellenceSection = ({ data }) => (
  <SideBySide
    bgColor={data.bgColor}
    textColor={data.textColor}
    leftFlex={0.5}
    rightFlex={1.5}
    gap="100px"
    leftContent={
      <h2
        style={{ fontSize: "3rem", fontWeight: "bold", lineHeight: "1.2", color: "#006666" }}
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
    }
    rightContent={
      <div style={{ lineHeight: "1.8" }}>
        <p style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>{data.paragraph1}</p>
        <p style={{ fontSize: "1.1rem" }}>{data.paragraph2}</p>
      </div>
    }
  />
);

// ---------------- COMPLIANCE ----------------
const Compliance = ({ data }) => {
  // Convert: "/assets/images/aboutUs3.png" → "/images/aboutUs3.png"
  const finalImage = data.image.replace("/assets", "");

  return (
    <SideBySide
      bgColor={data.bgColor}
      textColor={data.textColor}
      leftContent={
        <div>
          <h2 style={{ fontSize: "4rem", color: "#006666", marginBottom: "1rem" }}>
            {data.title}
          </h2>
          <p style={{ fontSize: "1.1rem", lineHeight: "1.7" }}>{data.text}</p>
        </div>
      }
      rightContent={
        <img
          src={`${CDN_BASE_URL}${finalImage}`}
          alt="Compliance"
          style={{
            width: "95%",
            height: "450px",
            borderRadius: "8px",
            objectFit: "cover",
          }}
        />
      }
    />
  );
};


// ---------------- BACKGROUND JOIN TEAM ----------------
const BackgroundSection = ({ data }) => {
  const navigate = useNavigate();

  // Convert: "/assets/images/aboutUs4.png" → "/images/aboutUs4.png"
  const finalImage = data.image.replace("/assets", "");

  return (
    <div className="join-team-wrap">
      <img
        className="join-team-image"
        src={`${CDN_BASE_URL}${finalImage}`}
        alt="Join Team"
        style={{ borderRadius: "8px" }}
      />

      <div
        className="join-team-overlay"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          color: data.textColor,
        }}
      >
        <h2 className="join-team-title" style={{ marginBottom: "15px" }}>
          {data.title}
        </h2>
        <p className="join-team-text" style={{ marginBottom: "20px" }}>
          {data.text}
        </p>

        <button
          className="join-team-btn"
          onClick={() => navigate("/contactUS")}
          style={{
            backgroundColor: data.buttonColor,
            color: "#fff",
            padding: "12px 25px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          {data.buttonText}
        </button>
      </div>
    </div>
  );
};


// ---------------- MAIN PAGE ----------------
const AboutUs = () => {
  const data = aboutData;

  return (
    <div>
      <HeroImage title={data.heroImage.title} background={data.heroImage.backgroundImage} />
      <WhoWeAre data={data.whoWeAre} />
      <MissionVision data={data.missionVision} />
      <WhyMedRCM data={data.whyMedRCM} />
      <Features data={data.features} />
      <ExcellenceSection data={data.excellence} />
      <Compliance data={data.compliance} />
      <BackgroundSection data={data.backgroundSection} />
    </div>
  );
};

export default AboutUs;
